/**
 * bmp-size-required.js — forced size selection.
 *
 * Ported from BuildMyPOD v2.0.0 (2026-08-28) but REIMPLEMENTED against this
 * theme's markup. The v2 original is ~830 lines, most of it reconciling
 * third-party swatch apps (Globo) that never touch the native radio. RGC uses
 * this theme's own native radios, so that machinery is dead weight and every
 * line of it is a failure mode on the add-to-cart path. Same three-layer
 * design, same attribute names, same CSS contract — smaller surface.
 *
 * It also does NOT import '@shopify/events': v1.1.3 has no such importmap
 * entry (the same blocker that forced the bmp-product-labels.js rewrite).
 * Nothing here needs it.
 *
 * Layer 1 (this file) refuses the interaction. Layer 2 (buy-buttons.liquid)
 * disables the hidden input[name="id"] so a native POST cannot fall back to a
 * variant. The render half is already native to this theme: it never emits
 * `checked` on a size radio.
 *
 * WINDOW, NOT DOCUMENT: this theme's declarative on:click dispatcher is itself
 * a document capture listener, and it is registered before this module loads.
 * Two capture listeners on the same node fire in registration order, so a
 * document listener here would run second — after the fetch had already gone.
 * window is the first node in the capture path, so it always precedes them.
 *
 * CLICK, NOT ONLY SUBMIT: the add-to-cart component runs its success animation
 * (data-added, fly-to-cart) on the CLICK, before a submit event exists.
 * Cancelling the click suppresses animation and submission together. The
 * submit listener stays as the belt for Enter-key and requestSubmit().
 *
 * This module sets state attributes and cancels events. It never rewrites
 * shopper-facing text — the prompt is a CSS custom property set in Liquid.
 */
const FORM_SELECTOR = 'product-form-component[data-bmp-size-required]';
const TRIGGER_SELECTOR = '.add-to-cart-button, [ref="addToCartButton"], button[type="submit"]';
const satisfied = new WeakSet();

/** @param {Element|null} el */
function resolveForm(el) {
  return el ? el.closest(FORM_SELECTOR) : null;
}

/**
 * Quick-add renders by FETCHING the product page at the card's own href, and
 * that href carries ?variant= for the card's default variant
 * (/products/x?variant=<Navy/S>). The server therefore sees selected_variant,
 * reports the gate already satisfied, and quick-add would add size S with the
 * shopper never having chosen — measured, not theorised.
 *
 * A card's default variant is not a choice. Inside quick-add the server's
 * verdict is ignored outright and only a real runtime interaction opens the
 * gate. On a genuine product page ?variant= still counts, so shared links and
 * ad landings keep working.
 */
function inQuickAdd(form) {
  return Boolean(form.closest('dialog, quick-add-dialog, quick-add-component'));
}

/** Server said the shopper already chose (?variant= was in the request). */
function isSatisfied(form) {
  // Runtime interaction is the ONLY signal. The server no longer emits a
  // satisfied state at all (see bmp-size-required-state.liquid); this stays
  // WeakSet-only so a stale attribute from a cached render cannot open a gate.
  return satisfied.has(form);
}

/** Monotone: once chosen, the gate never shuts again. */
function openGate(form) {
  satisfied.add(form);
  form.dataset.bmpSizeSatisfied = 'true';
  delete form.dataset.bmpSizeMissing;
  const hidden = form.querySelector('input[name="id"]');
  if (hidden) hidden.disabled = false;
}

function blockInteraction(form) {
  // Re-trigger the animation on a repeat click: drop the attribute, force a
  // reflow, then re-add. Without the reflow the browser coalesces the two
  // style changes and no animation plays the second time.
  delete form.dataset.bmpSizeMissing;
  void form.offsetWidth;
  form.dataset.bmpSizeMissing = 'true';
  const live = form.querySelector('[ref="liveRegion"]');
  if (live) {
    const prompt = getComputedStyle(form).getPropertyValue('--bmp-size-required-prompt').trim().replace(/^"|"$/g, '');
    if (prompt) live.textContent = prompt;
  }
  const fieldset = document.querySelector('[data-is-size-option]');
  if (fieldset) fieldset.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** A trusted click inside the size control is the shopper choosing. */
function onClickCapture(event) {
  if (event.isTrusted && event.target instanceof Element) {
    const inSize = event.target.closest('[data-is-size-option]');
    if (inSize) {
      const form = document.querySelector(FORM_SELECTOR);
      if (form) openGate(form);
    }
  }
  const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;
  if (!trigger) return;
  const form = resolveForm(trigger);
  if (!form) return;
  if (isSatisfied(form)) return openGate(form);
  event.preventDefault();
  event.stopImmediatePropagation();
  blockInteraction(form);
}

function onSubmitCapture(event) {
  const form = resolveForm(event.target instanceof Element ? event.target : null);
  if (!form) return;
  if (isSatisfied(form)) return openGate(form);
  event.preventDefault();
  event.stopImmediatePropagation();
  blockInteraction(form);
}

/** Keyboard selection and <select> pickers never produce a click on the input. */
function onChange(event) {
  const t = event.target;
  if (!(t instanceof Element)) return;
  if (!t.closest('[data-is-size-option]')) return;
  if (t instanceof HTMLSelectElement && t.value === '') return;
  const form = document.querySelector(FORM_SELECTOR);
  if (form) openGate(form);
}

/**
 * Undo the theme's own pre-selection.
 *
 * The SERVER never emits `checked` on a size radio — that guard is already
 * native to this theme. But the variant-picker JS syncs the control to
 * product.selected_or_first_available_variant after hydration and paints S as
 * chosen, and the selected-value label follows. A shopper then sees a size
 * already picked, clicks Add to cart, and gets refused — strictly worse than
 * no gate at all.
 *
 * So the paint is undone while the gate is shut. Repeated on a short schedule
 * because the repaint can land after hydration, after a section morph, or
 * after quick-add lifts the form into a modal; a single pass at load loses all
 * three races. Each pass is a no-op once the shopper has actually chosen.
 */
const DEPAINT_DELAYS_MS = [0, 250, 600, 1500, 3000];

function depaintPass() {
  for (const form of document.querySelectorAll(FORM_SELECTOR)) {
    if (isSatisfied(form)) continue;
    if (inQuickAdd(form) && form.dataset.bmpSizeSatisfied === 'true') delete form.dataset.bmpSizeSatisfied;
    const scope = form.closest('.shopify-section, dialog') ?? document;
    for (const group of scope.querySelectorAll('[data-is-size-option]')) {
      for (const input of group.querySelectorAll('input[type="radio"]')) {
        if (input.checked) input.checked = false;
      }
      for (const select of group.querySelectorAll('select')) {
        if (select.value !== '') select.value = '';
      }
      // Restore the label to its placeholder ("Please choose a size"), which
      // Liquid already put on the element for exactly this purpose.
      for (const el of group.querySelectorAll('[data-placeholder]')) {
        const placeholder = el.getAttribute('data-placeholder');
        if (placeholder && el.textContent.trim() !== placeholder) el.textContent = placeholder;
      }
    }
    // Layer 2 is a server-render attribute; a morph can drop it.
    const hidden = form.querySelector('input[name="id"]');
    if (hidden && !hidden.disabled) hidden.disabled = true;
  }
}

for (const delay of DEPAINT_DELAYS_MS) setTimeout(depaintPass, delay);
document.addEventListener('variant:update', () => setTimeout(depaintPass, 0));

// quick-add lifts a whole product form into a modal long after those timers
// have run, and a section morph can re-paint an existing one. Both arrive as
// DOM mutations, so watch for them rather than guessing another delay.
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.(FORM_SELECTOR) || node.querySelector?.(FORM_SELECTOR)) {
        setTimeout(depaintPass, 0);
        setTimeout(depaintPass, 300);
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

window.addEventListener('click', onClickCapture, { capture: true });
window.addEventListener('submit', onSubmitCapture, { capture: true });
document.addEventListener('change', onChange);
